import { AddonData, BillingUsageProduct } from '~/api/types/billingData.ts';
import {
  capitalizeStr,
  formatNumberShort,
  pluralizeUnit
} from '~/utils/commonUtils.ts';
import { twMerge } from 'tailwind-merge';

export type ProductUsageMarker = {
  title?: string;
  value: string;
  align?: 'start' | 'center' | 'end';
  position?: 'top' | 'bottom';
};

export type ProductUsageSegment = {
  value: number;
  colorClass?: string;
  marker?: ProductUsageMarker;
};

const UNAVAILABLE_FREE_TIER_QUANTITY = -1;

enum TierColors {
  USED_TIER_COLOR_CODE = `#4F5EFF`,
  FREE_TIER_COLOR_CODE = `#30A46C`,
  NO_USAGE_COLOR_CODE = `#E8E8E8`
}

const getTierColorClass = (tierColor: TierColors): string => {
  switch (tierColor) {
    case TierColors.USED_TIER_COLOR_CODE:
      return 'tier-blue';
    case TierColors.FREE_TIER_COLOR_CODE:
      return 'tier-green';
    case TierColors.NO_USAGE_COLOR_CODE:
      return 'tier-gray';
    default:
      return 'tier-gray';
  }
};

const getFreeTierQuantity = (product: BillingUsageProduct): number => {
  const freeTier = product.tiers.filter((tier) => tier.isFreeTier);
  return freeTier.length
    ? Number(freeTier[0].totalAvailableCount)
    : UNAVAILABLE_FREE_TIER_QUANTITY;
};

const getTotalUsage = (product: BillingUsageProduct): number => {
  return product.tiers.reduce((acc, tier) => {
    const usage = Math.max(Number(tier.currentCount), 0);
    return acc + usage;
  }, 0);
};

// these are very weak identifiers just to help us identify products in UI
// ideally we should have stable product codes from backend
// need to make it more robust in future
const BillingProductUiIdentifiers = {
  SSO: ['sso'],
  SCIM: ['scim'],
  ENTERPRISE_CONNECTIONS: ['enterprise', 'connection'],
  MAU: ['mau'],
  MAO: ['mao'],
  M2M_TOKENS: ['m2m', 'token'],
  TOOL_CALLS: ['tool', 'call'],
  CONNECTED_ACCOUNTS: ['connected', 'account'],
  CUSTOMIZATION_PRO: ['customization']
};

type BillingProductIdentifierKey = keyof typeof BillingProductUiIdentifiers;
type BillingProductIdentifier =
  (typeof BillingProductUiIdentifiers)[BillingProductIdentifierKey];

const BillingProductIdentifiersUnit: Partial<
  Record<BillingProductIdentifierKey, string>
> = {
  SSO: 'connection',
  SCIM: 'connection',
  ENTERPRISE_CONNECTIONS: 'connection',
  MAU: 'user',
  MAO: 'organization',
  M2M_TOKENS: 'token',
  TOOL_CALLS: 'call',
  CONNECTED_ACCOUNTS: 'account'
};

const isProductOfType = (
  product: BillingUsageProduct,
  identifier: BillingProductIdentifier
): boolean => {
  return identifier.every((idPart) =>
    product.productName.toLowerCase().includes(idPart)
  );
};

const isAddonOfType = (
  addon: AddonData,
  identifier: BillingProductIdentifier
): boolean => {
  return identifier.every((idPart) =>
    addon.name.toLowerCase().includes(idPart)
  );
};

const getUnitVerbiage = (product: BillingUsageProduct): string => {
  const keyName = Object.keys(BillingProductUiIdentifiers).find((key) =>
    isProductOfType(product, BillingProductUiIdentifiers[key])
  );
  return (keyName && BillingProductIdentifiersUnit[keyName]) || '';
};

type BillingUsageMarker = {
  value: number;
  title: string;
  description: string;
  align?: 'start' | 'center' | 'end';
  position?: 'top' | 'bottom';
  colorCode?: TierColors;
};

const getProductMarkersWithLabel = (product: BillingUsageProduct) => {
  const freeTierQuantity = getFreeTierQuantity(product);
  const totalUsageQuantity = getTotalUsage(product);
  const totalQuantity = Math.ceil(
    Math.max(freeTierQuantity, totalUsageQuantity) * 1.5
  );
  const freeTierValue = (freeTierQuantity / totalQuantity) * 100;
  const usedTierValue = (totalUsageQuantity / totalQuantity) * 100;

  const usageMarker: BillingUsageMarker = {
    value: usedTierValue,
    title: 'Current usage',
    description: `${formatNumberShort(totalUsageQuantity)} ${capitalizeStr(pluralizeUnit(getUnitVerbiage(product), totalUsageQuantity))}`,
    align: 'start',
    position: 'top'
  };

  const freeTierMarker: BillingUsageMarker = {
    value: 0,
    title: 'Free tier limit',
    description: `${formatNumberShort(
      freeTierQuantity
    )} ${capitalizeStr(pluralizeUnit(getUnitVerbiage(product), freeTierQuantity))}`,
    align: 'end',
    position: 'bottom',
    colorCode: TierColors.USED_TIER_COLOR_CODE
  };

  let markers: BillingUsageMarker[] = [];

  if (freeTierQuantity > totalUsageQuantity) {
    // free tier not yet reached
    freeTierMarker.align = 'end';
    freeTierMarker.colorCode = TierColors.NO_USAGE_COLOR_CODE;
    usageMarker.colorCode = TierColors.FREE_TIER_COLOR_CODE;
    markers = [usageMarker, freeTierMarker];
    usageMarker.value = usedTierValue;
    freeTierMarker.value = freeTierValue - usedTierValue;
  } else if (freeTierQuantity > 0 && freeTierQuantity <= totalUsageQuantity) {
    // free tier exceeded
    freeTierMarker.align = 'end';
    freeTierMarker.colorCode = TierColors.FREE_TIER_COLOR_CODE;
    usageMarker.colorCode = TierColors.USED_TIER_COLOR_CODE;
    markers = [freeTierMarker, usageMarker];
    freeTierMarker.value = freeTierValue;
    usageMarker.value = usedTierValue - freeTierValue;
  }

  if (totalUsageQuantity === 0) {
    freeTierMarker.colorCode = TierColors.NO_USAGE_COLOR_CODE;
  }

  return markers;
};

const getMarkerLayout = (
  align: ProductUsageMarker['align'],
  position: ProductUsageMarker['position']
) => {
  const resolvedAlign = align ?? 'center';
  const resolvedPosition = position ?? 'top';

  const translateXMap: Record<'start' | 'center' | 'end', string> = {
    start: 'translateX(0)',
    center: 'translateX(-50%)',
    end: 'translateX(-100%)'
  };

  const topTextClassMap: Record<'start' | 'center' | 'end', string> = {
    start: 'items-start text-left -top-1 left-2',
    center: 'items-center text-center -top-1',
    end: 'items-end text-right -top-1 right-2'
  };

  const bottomTextClassMap: Record<'start' | 'center' | 'end', string> = {
    start: 'items-start text-left top-3 left-2',
    center: 'items-center text-center top-3',
    end: 'items-end text-right top-3 right-2'
  };

  return {
    translateX: translateXMap[resolvedAlign],
    textBeforeLine: resolvedAlign === 'end',
    containerClass:
      resolvedPosition === 'bottom'
        ? 'absolute top-0 flex items-start gap-2 text-xs text-fg-muted'
        : 'absolute bottom-0 flex items-end gap-2 text-xs text-fg-muted',
    textClass: twMerge(
      'absolute z-10 flex flex-col gap-0.5 whitespace-nowrap text-xs text-fg-muted',
      resolvedPosition === 'bottom'
        ? bottomTextClassMap[resolvedAlign]
        : topTextClassMap[resolvedAlign]
    ),
    lineClass: 'block w-px bg-border-subtle h-10'
  };
};

export {
  BillingProductUiIdentifiers,
  TierColors,
  getTierColorClass,
  isProductOfType,
  isAddonOfType,
  getProductMarkersWithLabel,
  getMarkerLayout
};
